import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";

import "@fortawesome/fontawesome-free/css/all.min.css";
import "assets/styles/tailwind.css";
import "assets/styles/index.css";

import Admin from "../src/layouts/Admin.js";
import Login from "views/auth/Login.js";
import Nlc from "../src/views/admin/Nlc.js";
import Brands from "../src/views/admin/Brands.js";
import Products from "../src/views/admin/Products.js";
import Schemes from "../src/views/admin/Schemes.js";
import PriceDrop from "views/admin/PriceDrop.js";
import States from "views/admin/States.js";
import District from "views/admin/District.js";
import Groups from "views/admin/Groups.js";
import Subscriptions from "views/admin/Subscriptions.js";
import Users from "views/admin/Users.js";
import Posts from "views/admin/Posts.js";
import TOTP from "views/admin/TOTP.js";
import { ToastContainer } from "react-toastify";
import Whatsapp from "views/admin/Whatsapp.js";
import NotificationTopics from "views/admin/NotificationTopics.js";
import Notifications from "views/admin/Notifications.js";
import BulkSubscription from "views/admin/BulkSubscription.js";
import Files from "views/admin/Files.js";
import Analytics from "views/admin/Analytics.js";
import Share from "views/admin/Share.js";
import MydukanSubscriptions from "views/admin/MydukanSubscriptions.js";
import NlcV2 from "views/admin/NlcV2.js";
import { Provider } from "react-redux";
import store from "./redux/store.js";
import Complaints from "views/admin/Complaint.js";
import MyDukaanUsers from "./views/admin/MyDukaanUsers.js";
import HSN from "views/admin/Hsn.js";
import MyDukanBrands from "views/admin/MyDukanBrands.js";
import MyDukanProducts from "views/admin/MyDukanProducts.js";
import MyDukanPriceDrop from "views/admin/MyDukanPriceDrop.js";
import MyDukanBlocknUnblock from "views/admin/MyDukanBlocknUnblock.js";
import RetailerTypes from "views/admin/Retailer.js";
import NlcCalculator from "views/admin/NlcCalculator.js";
import SchemesV2 from "views/admin/SchemesV2.js";
import SchemesV3 from "views/admin/SchemesV3.js";
import Inventory from "views/admin/Inventory.js";

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if ([401, 403].includes(error?.response?.status)) {
      localStorage.removeItem("token");
      toast("Session expired. Please login again.", {
        type: "error",
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

const theme = createTheme({
  typography: {
    fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif",
  },
  palette: {
    primary: { main: "#4F46E5" },
    secondary: { main: "#64748B" },
    success: { main: "#059669" },
    error: { main: "#DC2626" },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: "0.5rem",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.875rem",
          height: 40,
          padding: "0 1.125rem",
          minWidth: 0,
        },
        sizeLarge: { height: 48 },
        sizeSmall: { height: 32, fontSize: "0.8rem", padding: "0 0.75rem" },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small", variant: "outlined" },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "0.5rem",
            fontSize: "0.875rem",
            background: "#F8FAFC",
          },
          "& .MuiInputLabel-root": { fontSize: "0.875rem" },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: "0.5rem", fontSize: "0.875rem" },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { borderRadius: "0.5rem", fontSize: "0.875rem", background: "#F8FAFC" },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: "0.875rem" } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: "0.875rem" },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 700,
          padding: "1.125rem 1.5rem",
          borderBottom: "1px solid #E2E8F0",
          color: "#0F172A",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: "1.25rem 1.5rem" } },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "0.875rem 1.5rem",
          borderTop: "1px solid #E2E8F0",
          gap: "0.5rem",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: "0.375rem", fontWeight: 500, fontSize: "0.8rem" },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: { "& .MuiPaginationItem-root": { borderRadius: "0.5rem", fontSize: "0.8rem" } },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: "0.875rem" } },
    },
    MuiFormLabel: {
      styleOverrides: { root: { fontSize: "0.875rem", fontWeight: 500 } },
    },
    MuiFormControlLabel: {
      styleOverrides: { label: { fontSize: "0.875rem" } },
    },
  },
});
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <ToastContainer />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="admin" element={<Admin />}>
            <Route path="nlc" element={<Nlc />} />
            <Route path="nlc-v2" element={<NlcV2 />} />
            <Route path="brands" element={<Brands />} />
            <Route path="totp" element={<TOTP />} />
            <Route path="products" element={<Products />} />
            <Route path="schemes" element={<Schemes />} />
            <Route path="schemesv2" element={<SchemesV2 />} />
            <Route path="schemesv3" element={<SchemesV3 />} />
            <Route path="price-drop" element={<PriceDrop />} />
            <Route path="states" element={<States />} />
            <Route path="district" element={<District />} />
            <Route path="retailer" element={<RetailerTypes />} />
            <Route path="connect" element={<Complaints />} />
            <Route path="groups" element={<Groups />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="bulkSubscription" element={<BulkSubscription />} />
            <Route path="users" element={<Users />} />
            <Route path="posts" element={<Posts />} />
            <Route path="files" element={<Files />} />
            <Route path="whatsapp" element={<Whatsapp />} />
            <Route path="inventory" element={<Inventory />} />
            <Route
              path="notification-topics"
              element={<NotificationTopics />}
            />
            <Route path="myDukanhsn" element={<HSN />} />
            <Route path="myDukanbrands" element={<MyDukanBrands />} />
            <Route path="myDukanproducts" element={<MyDukanProducts />} />
            <Route path="myDukanpricedrop" element={<MyDukanPriceDrop />} />
            <Route
              path="myDukanblocknunblock"
              element={<MyDukanBlocknUnblock />}
            />
            <Route path="nlcCalculator" element={<NlcCalculator />} />
            <Route path="share" element={<Share />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="analytics" element={<Analytics />} />

            <Route
              path="mydukanSubscriptions"
              element={<MydukanSubscriptions />}
            />
            <Route path="mydukanUsers" element={<MyDukaanUsers />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  </Provider>
);
